/**
 * Full-viewport canvas host for the WebGL mandala renderer.
 *
 * Owns: the <canvas> (fixed, inset 0, touch-action none, aria-hidden), ResizeObserver + devicePixelRatio
 * (capped at 1.5 on phones / 2 elsewhere, stepped down adaptively when the renderer's rolling frame time
 * exceeds 20 ms), context-loss bookkeeping, and renderer.mount/resize/unmount.
 *
 * Does NOT own the rAF loop or pointer events: the play screen calls renderer.frame() and forwards
 * pointerDown/Move/Up (see renderer/devPage.md).
 */
import { useEffect, useRef } from 'react';
import type { MandalaRenderer } from './renderer-api';
import { detectPhone, dprCapFor, nextDprScale } from './renderer/tier';

export interface MandalaViewProps {
  renderer: MandalaRenderer;
  /** reduced motion (prefers-reduced-motion or the in-app Calm toggle) */
  calm: boolean;
  /** optional class for the canvas (position/size are fixed inline) */
  className?: string;
}

const CANVAS_STYLE: React.CSSProperties = {
  position: 'fixed', inset: 0, width: '100%', height: '100%', display: 'block',
  touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', background: 'transparent',
};

export function MandalaView({ renderer, calm, className }: MandalaViewProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cap = dprCapFor(detectPhone());
    let scale = Math.min(cap, window.devicePixelRatio || 1);
    let cssW = canvas.clientWidth || window.innerWidth;
    let cssH = canvas.clientHeight || window.innerHeight;

    renderer.mount(canvas);
    const push = () => renderer.resize(cssW, cssH, scale);
    push();

    // size: ResizeObserver on the canvas (falls back to window resize)
    let ro: ResizeObserver | null = null;
    const onWindowResize = () => { cssW = canvas.clientWidth || window.innerWidth; cssH = canvas.clientHeight || window.innerHeight; push(); };
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        const e = entries[0];
        if (!e) return;
        const w = e.contentRect.width, h = e.contentRect.height;
        if (w <= 0 || h <= 0) return;
        cssW = w; cssH = h;
        push();
      });
      ro.observe(canvas);
    } else {
      window.addEventListener('resize', onWindowResize);
    }

    // devicePixelRatio changes (zoom / monitor move): re-evaluate the cap
    let dprMql: MediaQueryList | null = null;
    const watchDpr = () => {
      dprMql?.removeEventListener('change', onDprChange);
      dprMql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      dprMql.addEventListener('change', onDprChange);
    };
    const onDprChange = () => {
      scale = Math.min(cap, window.devicePixelRatio || 1, Math.max(scale, 0.75));
      push();
      watchDpr();
    };
    watchDpr();

    // adaptive DPR: poll the renderer's rolling frame time once per second
    let fastSeconds = 0;
    const perfTimer = window.setInterval(() => {
      const avg = renderer.perf?.avgFrameMs;
      if (avg === undefined || (renderer.perf?.frames ?? 0) < 30) return;
      fastSeconds = avg < 11 ? fastSeconds + 1 : 0;
      const hardCap = Math.min(cap, window.devicePixelRatio || 1);
      const next = nextDprScale(scale, avg, hardCap, fastSeconds);
      if (next !== scale) { scale = next; fastSeconds = 0; push(); }
    }, 1000);

    // context loss: the renderer cancels drawing and rebuilds itself; we re-issue the size afterwards
    const onLost = (e: Event) => { e.preventDefault(); };
    const onRestored = () => { push(); };
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onRestored);

    return () => {
      window.clearInterval(perfTimer);
      ro?.disconnect();
      window.removeEventListener('resize', onWindowResize);
      dprMql?.removeEventListener('change', onDprChange);
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      renderer.unmount();
    };
  }, [renderer]);

  useEffect(() => { renderer.setCalm?.(calm); }, [renderer, calm]);

  return <canvas ref={canvasRef} className={className} style={CANVAS_STYLE} aria-hidden="true" data-calm={calm ? '' : undefined} />;
}
